/**
 * Division Utilities for Scale Inspection App
 *
 * Handles parsing, rounding, and validation of scale readings
 * based on minimum division/increment values.
 */

/**
 * Parse division from various formats
 * @param {string|number} divisionInput - e.g., "20 lb", "0.05 kg", 0.001, 5
 * @returns {number} - Numeric division value
 */
export const parseDivision = (divisionInput) => {
  if (typeof divisionInput === 'number') return divisionInput;
  if (!divisionInput) return 1;

  const str = divisionInput.toString().trim();

  // Handle "20 lb", "0.05 kg", "0.001 g" formats
  const match = str.match(/^([\d.]+)/);
  return match ? parseFloat(match[1]) : 1;
};

/**
 * Parse unit from division string
 * @param {string} divisionInput - e.g., "20 lb", "0.05 kg"
 * @returns {string} - Unit string (lb, kg, g, etc.)
 */
export const parseUnit = (divisionInput) => {
  if (!divisionInput) return 'lb';
  const str = divisionInput.toString().trim();
  const match = str.match(/[\d.]+\s*(.+)/);
  return match ? match[1].trim() : 'lb';
};

/**
 * Get decimal places needed for a division
 * @param {string|number} division - Division value
 * @returns {number} - Number of decimal places (0.001 → 3, 0.05 → 2, 20 → 0)
 */
export const getDecimalPlaces = (division) => {
  const num = parseDivision(division);
  if (num >= 1) return 0;
  const str = num.toString();
  const decimalIndex = str.indexOf('.');
  return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1;
};

/**
 * Round value to nearest division
 * @param {number} value - Value to round
 * @param {string|number} division - Division to round to
 * @returns {number} - Rounded value
 */
export const roundToDivision = (value, division) => {
  const div = parseDivision(division);
  if (div === 0) return value;
  const rounded = Math.round(value / div) * div;
  // Fix floating point precision issues
  const decimals = getDecimalPlaces(division);
  return parseFloat(rounded.toFixed(decimals));
};

/**
 * Format value with appropriate decimal places for division
 * @param {number} value - Value to format
 * @param {string|number} division - Division for decimal precision
 * @returns {string} - Formatted value string
 */
export const formatToDivision = (value, division) => {
  if (value === '' || value === null || value === undefined) return '';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  const rounded = roundToDivision(numValue, division);
  const decimals = getDecimalPlaces(division);
  return rounded.toFixed(decimals);
};

/**
 * Validate if a value aligns with division
 * @param {number} value - Value to validate
 * @param {string|number} division - Division to check against
 * @returns {boolean} - True if value is valid for this division
 */
export const isValidDivisionValue = (value, division) => {
  if (value === '' || value === null || value === undefined) return true;
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return false;

  const div = parseDivision(division);
  if (div === 0) return true;

  // Use decimal-safe comparison to avoid floating point issues
  // Multiply both values to work with integers, then check divisibility
  const decimals = getDecimalPlaces(division);
  const multiplier = Math.pow(10, decimals);
  const scaledValue = Math.round(numValue * multiplier);
  const scaledDiv = Math.round(div * multiplier);

  const remainder = scaledValue % scaledDiv;
  // Allow tiny rounding errors (within 1 unit of scaled value)
  return remainder === 0 || remainder === scaledDiv || Math.abs(remainder) <= 1;
};

/**
 * Get the nearest valid division value
 * @param {number} value - Value to correct
 * @param {string|number} division - Division to align to
 * @returns {number} - Nearest valid value
 */
export const getNearestValidValue = (value, division) => {
  return roundToDivision(value, division);
};

/**
 * Generate range of valid values centered on a target (for scroll picker)
 * @param {number} centerValue - Value to center on (usually test load)
 * @param {string|number} division - Scale division
 * @param {number} capacity - Scale capacity (max value)
 * @param {object} options - Configuration options
 * @returns {number[]} - Array of valid division values
 */
export const generateDivisionRange = (centerValue, division, capacity, options = {}) => {
  const {
    rangeMultiplier = 50,  // Number of divisions in each direction
    maxItems = 200         // Maximum items to prevent performance issues
  } = options;

  const div = parseDivision(division);
  const decimals = getDecimalPlaces(division);

  // Calculate range (±rangeMultiplier divisions or 10% of capacity, whichever is smaller)
  const range = Math.min(div * rangeMultiplier, capacity * 0.1);
  const values = [];

  const start = Math.max(0, roundToDivision(centerValue - range, div));
  const end = Math.min(capacity, roundToDivision(centerValue + range, div));

  // Safety check for very small divisions
  const expectedCount = Math.ceil((end - start) / div);
  if (expectedCount > maxItems) {
    // Reduce range for very fine divisions
    const adjustedRange = (maxItems / 2) * div;
    const adjStart = Math.max(0, roundToDivision(centerValue - adjustedRange, div));
    const adjEnd = Math.min(capacity, roundToDivision(centerValue + adjustedRange, div));

    for (let v = adjStart; v <= adjEnd; v = parseFloat((v + div).toFixed(decimals))) {
      values.push(parseFloat(v.toFixed(decimals)));
    }
  } else {
    for (let v = start; v <= end; v = parseFloat((v + div).toFixed(decimals))) {
      values.push(parseFloat(v.toFixed(decimals)));
    }
  }

  return values;
};

/**
 * Step a value up or down by one division
 * @param {number|string} currentValue - Current value (or empty for default)
 * @param {number} defaultValue - Default value if current is empty (usually test load)
 * @param {string|number} division - Scale division
 * @param {number} capacity - Scale capacity
 * @param {number} direction - 1 for up, -1 for down
 * @returns {string} - New formatted value
 */
export const stepByDivision = (currentValue, defaultValue, division, capacity, direction) => {
  const div = parseDivision(division);
  const decimals = getDecimalPlaces(division);

  // If empty, start from default (test load)
  const startValue = (currentValue === '' || currentValue === null || currentValue === undefined)
    ? defaultValue
    : parseFloat(currentValue);

  if (isNaN(startValue)) return formatToDivision(defaultValue, division);

  // Calculate new value
  let newValue = roundToDivision(startValue + (direction * div), division);

  // Clamp to valid range
  newValue = Math.max(0, Math.min(capacity, newValue));

  return newValue.toFixed(decimals);
};

/**
 * Parse a capacity string to a number
 * @param {string|number} capacity - e.g., "10000 lb", "500 g", 10000
 * @returns {number} - Numeric capacity value
 */
export const parseCapacity = (capacity) => {
  if (typeof capacity === 'number') return capacity;
  if (!capacity) return 10000; // Default fallback

  const str = capacity.toString().trim();
  const match = str.match(/^([\d.]+)/);
  return match ? parseFloat(match[1]) : 10000;
};

/**
 * Trigger haptic feedback if available
 */
export const triggerHaptic = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate(10); // Short 10ms vibration
  }
};
